import { useEffect, useRef } from 'react';

import { PREVIEW_MODE } from '../preview/preview-mode';

/**
 * Realtime-Verbindung zum Backend.
 *
 * Sie liefert fachliche Ereignisse - Klingeln, Verbunden, Beendet - und
 * dient zugleich als Lebenszeichen des Browsers. Bricht sie während eines
 * Gesprächs ab, beginnt serverseitig die Reconnect-Frist
 * (06_telephony_sip_inbound.md).
 *
 * Authentifiziert wird über dasselbe Sitzungscookie wie bei REST; das
 * Backend schließt die Verbindung, sobald die Sitzung endet.
 */
export interface RealtimeEvent {
  event: string;
  payload: Record<string, unknown>;
}

export function useRealtime(onEvent: (event: RealtimeEvent) => void, enabled = true): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled || PREVIEW_MODE) {
      // In der Vorschau gibt es kein Backend, zu dem sich verbinden liesse.
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let closed = false;

    const connect = (): void => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(protocol + '//' + window.location.host + '/ws');

      socket.addEventListener('message', (message) => {
        try {
          handler.current(JSON.parse(String(message.data)) as RealtimeEvent);
        } catch {
          // Unlesbare Nachrichten werden verworfen.
        }
      });

      socket.addEventListener('close', (event) => {
        // 4001 bedeutet: die Sitzung ist beendet - dann nicht erneut verbinden.
        if (closed || event.code === 4001) {
          return;
        }
        reconnectTimer = window.setTimeout(connect, 2_000);
      });
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [enabled]);
}
